import axios from 'axios';

// Cliente de API de VIAGENS (entidade VGM, server-authoritative — Fase 1).
// Consome o router /api/operations/trips (backend Plan 01). O token JWT é
// anexado globalmente via axios.defaults pelo AuthContext — NÃO anexar aqui.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const tripsApi = {
  // Lista viagens de um equipamento (mais recentes primeiro). Filtro opcional por status.
  listTrips: (equipmentId, params = {}) =>
    axios.get(`${API}/operations/trips`, { params: { equipment_id: equipmentId, ...params } }),

  // Lê uma viagem por id.
  getTrip: (id) => axios.get(`${API}/operations/trips/${id}`),

  // Cria uma viagem PLANEJADA. body: { equipment_id, planned: { origin, stops:[{route_id, ordem, produto}], modo_carga } }
  createTrip: (data) => axios.post(`${API}/operations/trips`, data),

  // Edita uma viagem planejada. body: { planned, reason? }
  updateTrip: (id, data) => axios.put(`${API}/operations/trips/${id}`, data),

  // Cancela uma viagem (remoção lógica + motivo).
  cancelTrip: (id, reason) => axios.post(`${API}/operations/trips/${id}/cancel`, { reason }),

  // Vincula manualmente a planejada ao ciclo ATIVO do equipamento (LIVE) — 409 se não há
  // ciclo ativo ou se já vinculado a outra viagem (server-authoritative; CC-2026-07-02-006).
  linkCycle: (id) => axios.post(`${API}/operations/trips/${id}/link-cycle`),

  // Exclui uma viagem PLANEJADA (hard-delete). Qualquer outro status → 409.
  deleteTrip: (id) => axios.delete(`${API}/operations/trips/${id}`),

  // Lê o schedule (janela móvel) de um equipamento.
  getSchedule: (equipmentId) =>
    axios.get(`${API}/operations/trips/schedule`, { params: { equipment_id: equipmentId } }),

  // Re-ancora/reordena a janela móvel. body: { equipment_id, ordered_trip_ids, window_anchor_refuel_id? }
  updateSchedule: (data) => axios.put(`${API}/operations/trips/schedule`, data),

  // Fila encadeada "quando abastecer" (F2, read-only). Resposta:
  // { equipment_id, saldo_inicial_l, saldo_error, reserve_l,
  //   trips:[{ trip_id, destino, modo_carga, fuel_by_stage_l, consumo_l, saldo_volta_l, refuel_here }],
  //   first_refuel_trip_id }
  // Toda a aritmética de combustível é do backend — o front SÓ exibe (INV-UI-TRAIL-001).
  getQueue: (equipmentId) =>
    axios.get(`${API}/operations/trips/queue`, { params: { equipment_id: equipmentId } }),

  // Lista do CONSOLE multi-equipamento (Fase 5). Escopo resolvido no backend por RBAC.
  // params opcionais: { equipment_id, status, start_after, start_before }
  listConsole: (params = {}) =>
    axios.get(`${API}/operations/trips/console`, { params }),

  // Destinos: fonte de verdade lab_routes (NÃO hardcode de portos).
  getRoutes: () => axios.get(`${API}/lab/routes`),

  // --- Ciclo de despacho + dados operacionais (Fase 6, Plan 02) ---

  // Upsert dos dados operacionais da viagem (D-17). body: { volume_t?, volume_source?, crew?[{nome,funcao}], observacoes? }
  // 409 se fechada/cancelada (correções via errata); 422 se volume<=0. updated_by vem do JWT no backend (D-09).
  updateOperationalData: (id, data) =>
    axios.put(`${API}/operations/trips/${id}/operational-data`, data),

  // Abre o despacho (D-02): marca dispatch.opened_* append-only no doc. NÃO altera status (worker é dono). Idempotente.
  openDispatch: (id) =>
    axios.post(`${API}/operations/trips/${id}/open-dispatch`),

  // Fecha/sela o despacho (D-04): reusa o endpoint existente confirm-closure (fechamento_proposto→fechada).
  confirmClosure: (id) =>
    axios.post(`${API}/operations/trips/${id}/confirm-closure`),

  // Lançamento manual de horímetro/combustível/tempo para EQUIPAMENTO VIRTUAL (D-14/D-05).
  // body: { fuel_total_l, started_at, ended_at, horimetro?, fuel_by_motor?, fuel_by_stage_l? }.
  // GATED a virtual no backend (viagem real → 409); valores inválidos → 422. updated_by vem do JWT.
  // O status NUNCA é mutado pelo cliente — o backend retorna fechamento_proposto após o lançamento.
  manualActual: (id, data) =>
    axios.put(`${API}/operations/trips/${id}/manual-actual`, data),

  // Lê um equipamento por id → EquipmentResponse (carrega cargo_capacity_t por modo de carga, após Plan 01).
  // ATENÇÃO: path é /api/equipments/{id} (sem /operations). É a ÚNICA fonte do pré-preenchimento de volume (D-11/D-12);
  // o doc da viagem (GET /{trip_id}) NÃO traz cargo_capacity_t (projeta só name/tag do equipment).
  getEquipment: (id) => axios.get(`${API}/equipments/${id}`),

  // --- Anexos + observações (Phase 9, REQ-29/REQ-11) ---

  // Lista os anexos/observações da viagem (sem bytes). Resposta:
  // { attachments:[{id, kind, filename, content_type, size, note, uploaded_by, uploaded_at, has_file}] }
  listAttachments: (tripId) =>
    axios.get(`${API}/operations/trips/${tripId}/attachments`),

  // Anexa um arquivo/imagem E/OU nota textual. `formData` = FormData com `file` e/ou `note` (e `kind` opcional).
  // multipart/form-data; uploaded_by vem do JWT no backend. 409 se a viagem está fechada/cancelada.
  uploadAttachment: (tripId, formData) =>
    axios.post(`${API}/operations/trips/${tripId}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  // Baixa os bytes de um anexo como blob (mantém o JWT global do axios — evita token na URL).
  downloadAttachment: (tripId, attachmentId) =>
    axios.get(`${API}/operations/trips/${tripId}/attachments/${attachmentId}/download`, {
      responseType: 'blob',
    }),

  // Exclui um anexo (bytes + metadados). 409 se a viagem está fechada/cancelada.
  deleteAttachment: (tripId, attachmentId) =>
    axios.delete(`${API}/operations/trips/${tripId}/attachments/${attachmentId}`),

  // --- Briefing climático (Phase 9, REQ-30) ---

  // Briefing climático da janela saída→retorno. Resposta:
  // { archived:boolean, briefing:{ status, alert, alert_text, window, locations:[...] } }.
  // archived=true ⇒ snapshot imutável de viagem fechada; archived=false ⇒ ao vivo.
  // INV-UI-TRAIL-001: o front SÓ exibe — nenhuma reavaliação de clima no cliente.
  getWeatherBriefing: (tripId) =>
    axios.get(`${API}/operations/trips/${tripId}/weather-briefing`),

  // --- Relatórios agregados + export XLSX (Phase 10, REQ-33/REQ-34) ---

  // Relatório consolidado de viagens fechadas agrupado por período/equipamento/área.
  // params: { start_after?, start_before?, equipment_id?, group_by: 'period'|'equipment'|'area' }
  // Resposta: { group_by, groups:[{ key, label, num_viagens, volume_t_total, consumo_l_total,
  //   duracao_total_h, duracao_media_h }], totais:{...} }
  // RBAC 100% no backend (JWT) — o cliente NÃO envia o conjunto visível.
  // INV-UI-TRAIL-001: toda a aritmética é server-side; o front SÓ exibe os números prontos.
  getAggregate: (params = {}) =>
    axios.get(`${API}/operations/reports/aggregate`, { params }),

  // Export XLSX das viagens fechadas (1 linha por viagem, planilha nativa para Excel).
  // params: { start_after?, start_before?, equipment_id? }
  // Download via blob mantendo o JWT global do axios (sem token na URL) — espelha downloadAttachment.
  // INV-UI-TRAIL-001: o XLSX é produzido 100% pelo backend; o cliente só aciona o download.
  exportXlsx: (params = {}) =>
    axios.get(`${API}/operations/reports/export.xlsx`, { params, responseType: 'blob' }),
};

export default tripsApi;
